import type { SegmentedControlOption } from '@/components/ui';

/**
 * How a line's discount was ENTERED — and therefore which of the two mutually
 * exclusive columns the wire carries.
 *
 * ⚠️ **This module is WORDING AND NOTHING ELSE.** It decides no payload, no
 * refusal and no arithmetic. `discountWriteOf` in each grid still turns the
 * mode into the `{discount, discountAmount}` pair, and each grid keeps its own
 * validation — those refusal sets genuinely differ between the three, and
 * unifying them is a behaviour change rather than a refactor.
 */
export type DiscountMode = 'percentage' | 'amount';

/**
 * The two options, in one place, because the owner's instruction was that the
 * three grids must not word this choice differently.
 *
 * ⚠️ **NEVER label a mode with a CURRENCY CODE.** The amount option used to
 * render the document's currency (`EUR`), which made the control say three
 * different things on three documents, say `Amt` on a document whose company
 * was not yet chosen, and — the actual defect — read as a unit annotation
 * rather than as one of two choices. The label names the KIND of figure the box
 * takes; the currency belongs on the figure, not on the switch.
 *
 * ⚠️ **There are exactly TWO, and there is no third.** `f_quote_products`,
 * `f_invoice_products` and `f_deal_products` each carry `discount` and
 * `discount_amount` and nothing else, and sending both non-zero is a 422 naming
 * `discountAmount`. A "none" option would be a third state the columns cannot
 * express — an empty box already says it.
 */
export const DISCOUNT_MODE_OPTIONS: readonly SegmentedControlOption<DiscountMode>[] = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'amount', label: 'Amount' },
];

/** Names the CHOICE for assistive technology, never the current answer. */
export const DISCOUNT_MODE_GROUP_LABEL = 'Enter this discount as';

/**
 * ⚠️ **`null` is the percentage and a string is the amount** — including `''`.
 * An emptied amount box is a real zero on the amount column, not a fall back to
 * the percentage, so this must test for `null` and never for truthiness.
 */
export function discountModeOf(discountAmount: string | null): DiscountMode {
  return discountAmount === null ? 'percentage' : 'amount';
}

/**
 * Has the reader actually PUT this line in percentage mode?
 *
 * 🔴 **THIS IS NOT `discountModeOf(...) === 'percentage'`, AND THE DIFFERENCE IS
 * THE COMMONEST LINE IN THE GRID.** A line nobody has discounted carries
 * `discountAmount === null`, so the switch above reads `percentage` on every
 * untouched row — the mode is the DEFAULT there, not a choice. A chosen
 * percentage is one somebody typed.
 *
 * Owner's decision, 2026-09-13, alongside the instruction that typing an Amount
 * must stop flipping the mode: **a line with no discount has no mode to
 * preserve.** So the Amount cell solves an exact `discountAmount` on a fresh
 * line as it always has, and the lossy percentage arithmetic is confined to
 * rows somebody deliberately put in percentage mode. Read
 * `solveLineAmount`'s `keepPercentage` for what that arithmetic costs — on an
 * 18,000 line the reachable subtotals are £1.80 apart.
 *
 * ⚠️ **`'0'` and `'0.00'` are NOT a chosen percentage.** A zero discount is
 * indistinguishable from none on the wire — the column's default is `0.00` —
 * so treating it as chosen would make every line the server has ever defaulted
 * behave as though somebody had picked percentage. It is parsed rather than
 * string-compared for that reason: `0`, `0.0`, `0.00` and `00.0` are one fact.
 *
 * ⚠️ **STILL WORDING'S NEIGHBOUR, NOT ARITHMETIC.** This module's header says it
 * decides no payload, no refusal and no arithmetic, and that holds: this
 * answers a question about the MODE — the module's whole subject — and returns
 * a boolean. It computes no money and builds no `{discount, discountAmount}`
 * pair; `discountWriteOf` in each grid still does that.
 */
export function percentageModeIsChosen(
  discountAmount: string | null,
  discount: string,
): boolean {
  if (discountModeOf(discountAmount) !== 'percentage') return false;
  const typed = discount.trim();
  if (typed === '') return false;
  const parsed = Number(typed);
  return Number.isFinite(parsed) && parsed > 0;
}
